import { useStore } from '@/store';
import authService from '@/services/auth/auth.service';
import { EPermissoes } from '@/enums/ePermissoes';

class PermissaoService {
  private getRoles(): string[] {
    try {
      const rolesAny = authService.getRolesFromTokenNonAsync().role;
      if (Array.isArray(rolesAny)) return (rolesAny as string[]).map((x) => String(x).toLowerCase());
      if (typeof rolesAny === 'string' && rolesAny) return [rolesAny.toLowerCase()];
      return [];
    } catch {
      return [];
    }
  }

  private getEmail(): string {
    try {
      const email = authService.getRolesFromTokenNonAsync().email;
      return typeof email === 'string' ? email.trim().toLowerCase() : '';
    } catch {
      return '';
    }
  }

  temRoleAdmin(): boolean {
    return this.getRoles().some((x) => x == 'admin');
  }

  temAcessoPrivilegiado(): boolean {
    const roles = this.getRoles();
    return roles.some((x) => x == 'admin') || roles.some((x) => x == 'suporte');
  }

  // Fail-open: lista vazia = ainda nao carregada / backend antigo.
  tipoFornecimentoAtivo(utlTipoFornecimentoId: number): boolean {
    const ativos = useStore().getters.getTiposFornecimentoAtivos as number[];
    if (!Array.isArray(ativos) || ativos.length === 0) return true;
    return ativos.includes(utlTipoFornecimentoId);
  }

  private getUtlTipoFornecimentoIds(meta: any): number[] {
    const value = meta?.utlTipoFornecimentoId ?? meta?.UtlTipoFornecimentoId;
    if (value == null) return [];
    const ids = Array.isArray(value) ? value : [value];
    return ids.map(Number).filter((id) => !Number.isNaN(id));
  }

  temAcessoRota(meta: any): boolean {
    if (meta?.onlyAdmin) {
      return this.temRoleAdmin();
    }

    // Lista hardcoded de e-mails: sobrepõe permissões de grupo e o bypass de admin/suporte.
    if (Array.isArray(meta?.emailsPermitidos)) {
      const email = this.getEmail();
      return !!email && meta.emailsPermitidos.some((x: string) => x.toLowerCase() === email);
    }

    // Gate por tipo ativo antes do bypass de admin/suporte.
    const utlTipoFornecimentoIds = this.getUtlTipoFornecimentoIds(meta);
    if (utlTipoFornecimentoIds.length > 0 && !utlTipoFornecimentoIds.some((id) => this.tipoFornecimentoAtivo(id))) {
      return false;
    }

    if (this.temAcessoPrivilegiado()) return true;
    return this.temAlgumaPermissaoDoMeta(meta);
  }

  temAlgumaPermissaoDoMeta(meta: any): boolean {
    if (!meta || !meta.permissions || meta.permissions.length == 0) return true;
    const permissoesUsuario = useStore().getters.getPermissoes;
    const roles = authService.getRolesFromTokenNonAsync().role;
    if (Array.isArray(roles) && ((roles as string[]).find((x) => x.toLowerCase() == 'admin') || (roles as string[]).find((x) => x.toLowerCase() == 'suporte'))) return true;
    if (!Array.isArray(meta.permissions) || meta.permissions.length === 0) return true;

    const utlTipoFornecimentoIds = this.getUtlTipoFornecimentoIds(meta);
    return permissoesUsuario.some((p) => {
      if (!meta.permissions.includes(p.segItemPermissaoId)) return false;
      if (p.utlTipoFornecimentoId == null) return true;
      if (p.utlTipoFornecimentoId === meta.utlTipoFornecimentoId) return true;
      if (utlTipoFornecimentoIds.length === 0) return true;
      // Rotas multi-tipo (ex.: dashboard/relatórios de utilidades) declaram opcoesTipoFornecimento
      // em vez de um utlTipoFornecimentoId único: basta ter a permissão para algum dos tipos oferecidos.
      if (meta.opcoesTipoFornecimento) {
        return Object.keys(meta.opcoesTipoFornecimento).some((id) => Number(id) === p.utlTipoFornecimentoId);
      }
      return utlTipoFornecimentoIds.includes(p.utlTipoFornecimentoId);
    });
  }

  temPermissao(permissao: EPermissoes, utlTipoFornecimentoId: number | null = null) {
    const permissoesUsuario = useStore().getters.getPermissoes;
    if (utlTipoFornecimentoId == null) {
      return permissoesUsuario.some((p) => p.segItemPermissaoId == permissao);
    } else {
      return permissoesUsuario.some((p) =>
        p.segItemPermissaoId == permissao &&
        (p.utlTipoFornecimentoId == null || p.utlTipoFornecimentoId == utlTipoFornecimentoId)
      );
    }
  }
}

export default new PermissaoService();
